/**
 * PostcodeGeocoder Module
 * Converts UK postcodes to latitude/longitude coordinates
 * Uses postcodes.io (free) as primary, with Google Geocoding API as fallback
 */

// UK postcode validation regex
const UK_POSTCODE_REGEX = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;

/**
 * Validates UK postcode format
 * @param {string} postcode - The postcode to validate
 * @returns {boolean} - True if valid UK postcode format
 */
export function isValidPostcode(postcode) {
  return UK_POSTCODE_REGEX.test(postcode.trim());
}

/**
 * Normalizes postcode to uppercase without spaces
 * @param {string} postcode - The postcode to normalize
 * @returns {string} - Normalized postcode
 */
export function normalizePostcode(postcode) {
  return postcode.trim().toUpperCase().replace(/\s+/g, '');
}

/**
 * Geocodes a UK postcode using postcodes.io API
 * @param {string} postcode - The UK postcode to geocode
 * @returns {Promise<{lat: number, lng: number}>} - Coordinates
 * @throws {Error} - If postcode is invalid or geocoding fails
 */
async function geocodeWithPostcodesIO(postcode) {
  const normalized = normalizePostcode(postcode);
  const url = `https://api.postcodes.io/postcodes/${encodeURIComponent(normalized)}`;

  try {
    const response = await fetch(url);
    const data = await response.json();

    if (data.status === 200 && data.result) {
      return {
        lat: data.result.latitude,
        lng: data.result.longitude
      };
    } else if (data.status === 404) {
      throw new Error(`Postcode not found: ${postcode}`);
    } else {
      throw new Error(`Geocoding failed: ${data.error || 'Unknown error'}`);
    }
  } catch (error) {
    if (error.message.includes('Postcode not found')) {
      throw error;
    }
    throw new Error(`Failed to connect to geocoding service: ${error.message}`);
  }
}

/**
 * Geocodes a UK postcode using Google Geocoding API (fallback)
 * Note: Requires API key via Cloudflare Worker proxy
 * @param {string} postcode - The UK postcode to geocode
 * @returns {Promise<{lat: number, lng: number}>} - Coordinates
 * @throws {Error} - If geocoding fails
 */
async function geocodeWithGoogle(postcode) {
  // This will be proxied through Cloudflare Worker to hide API key
  const url = `/api/geocode?postcode=${encodeURIComponent(postcode)}`;

  try {
    const response = await fetch(url);
    const data = await response.json();

    if (data.status === 'OK' && data.results && data.results.length > 0) {
      const location = data.results[0].geometry.location;
      return {
        lat: location.lat,
        lng: location.lng
      };
    } else if (data.status === 'ZERO_RESULTS') {
      throw new Error(`Postcode not found: ${postcode}`);
    } else {
      throw new Error(`Geocoding failed: ${data.status}`);
    }
  } catch (error) {
    throw new Error(`Google geocoding fallback failed: ${error.message}`);
  }
}

/**
 * Main geocoding function with fallback strategy
 * Attempts postcodes.io first, falls back to Google if needed
 * @param {string} postcode - The UK postcode to geocode
 * @returns {Promise<{lat: number, lng: number, postcode: string}>} - Coordinates and normalized postcode
 * @throws {Error} - If postcode is invalid or all geocoding attempts fail
 */
export async function geocode(postcode) {
  // Validate format first
  if (!isValidPostcode(postcode)) {
    throw new Error(`Invalid UK postcode format: ${postcode}`);
  }

  const normalized = normalizePostcode(postcode);

  try {
    // Try postcodes.io first (free, no API key needed)
    const coords = await geocodeWithPostcodesIO(postcode);
    return {
      ...coords,
      postcode: normalized
    };
  } catch (primaryError) {
    // If postcodes.io fails with "not found", don't try Google
    if (primaryError.message.includes('Postcode not found')) {
      throw primaryError;
    }

    // Try Google Geocoding as fallback
    try {
      const coords = await geocodeWithGoogle(postcode);
      return {
        ...coords,
        postcode: normalized
      };
    } catch (fallbackError) {
      // Both services failed
      throw new Error(
        `Unable to geocode postcode ${postcode}. Primary: ${primaryError.message}. Fallback: ${fallbackError.message}`
      );
    }
  }
}

/**
 * Batch geocode multiple postcodes
 * @param {string[]} postcodes - Array of UK postcodes
 * @returns {Promise<Array<{lat: number, lng: number, postcode: string}>>} - Array of coordinates
 * @throws {Error} - If any postcode fails to geocode
 */
export async function geocodeBatch(postcodes) {
  const results = [];
  const errors = [];

  for (let i = 0; i < postcodes.length; i++) {
    try {
      const result = await geocode(postcodes[i]);
      results.push(result);
    } catch (error) {
      errors.push({
        postcode: postcodes[i],
        error: error.message,
        index: i
      });
    }
  }

  if (errors.length > 0) {
    throw new Error(
      `Failed to geocode ${errors.length} postcode(s): ${errors.map(e => e.postcode).join(', ')}`
    );
  }

  return results;
}
